import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText } from './common.js'

/**
 * Phone numbers are stored as typed, not normalised to E.164, because Gulf
 * garages routinely hold numbers from a dozen countries in a dozen formats and
 * silently rewriting them loses information. `toWaMeDigits` exists for wa.me.
 */
export const phoneSchema = z.string().trim().min(4).max(32)

/**
 * A phone number the workshop may simply not have yet.
 *
 * A car arrives, somebody gives a first name, and that is all anyone knows.
 * Demanding a number then buys nothing — it gets a placeholder typed into it,
 * which is worse than a blank: a blank is honestly empty, and "000" looks like
 * a number until someone tries to ring it.
 *
 * Still validated when there IS one, so a half-typed number is caught rather
 * than stored.
 */
export const optionalPhoneSchema = z
  .string()
  .trim()
  .max(32)
  .default('')
  .refine((value) => value === '' || value.length >= 4, {
    message: 'Phone number is too short',
  })

export const CLIENT_TYPES = ['individual', 'company'] as const
export type ClientType = (typeof CLIENT_TYPES)[number]
export const clientTypeSchema = z.enum(CLIENT_TYPES)

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  individual: 'Individual',
  company: 'Company',
}

/**
 * Free text that may legitimately be blank.
 *
 * The per-type fields are kept as EMPTY STRINGS rather than nulls so that
 * `Client` stays one flat object type. A discriminated union would be purer,
 * but every consumer — list, search, PDF, dashboard — would then have to narrow
 * before reading a name, for no real gain. Which fields must be filled is
 * enforced by the refinement below instead.
 */
const blankable = (max: number) => z.string().trim().max(max).default('')

const clientFields = {
  type: clientTypeSchema.default('individual'),

  // Individuals
  firstName: blankable(80),
  lastName: blankable(80),

  // Companies
  companyName: blankable(160),
  /** Tax Registration Number. Printed on a B2B tax invoice. */
  trn: blankable(40),
  tradeLicence: blankable(40),
  /** Printed in the customer block of a tax invoice. */
  billingAddress: blankable(500),

  // Both
  phone: optionalPhoneSchema,
  /** null means "same as phone" — never store a copy of phone here. */
  whatsappPhone: phoneSchema.nullable().default(null),
  notes: optionalText(2000),
}

/**
 * An individual needs a name; a company needs a company name. Enforced here so
 * neither shape can be saved half-filled, without forcing every reader to
 * narrow a union.
 */
function requirePerType(value: z.infer<z.ZodObject<typeof clientFields>>, ctx: z.RefinementCtx) {
  if (value.type === 'individual') {
    /*
     * A first name is the minimum: a person with no name at all cannot be
     * found again, and every screen that lists them would show a blank row.
     *
     * A surname is NOT required. The workshop often has only what was said at
     * the counter, and forcing the field means a placeholder gets typed into
     * it — which then prints on an invoice and is searched for by staff who do
     * not know it is fictional. An empty surname is honest, and
     * clientDisplayName already trims to just the first name.
     */
    if (!value.firstName) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['firstName'], message: 'First name is required' })
    }
  } else if (!value.companyName) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['companyName'],
      message: 'Company name is required',
    })
  }
}

export const clientInputSchema = z.object(clientFields).superRefine(requirePerType)
export type ClientInput = z.infer<typeof clientInputSchema>

export const clientSchema = z
  .object({
    ...clientFields,
    id: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .superRefine(requirePerType)
export type Client = z.infer<typeof clientSchema>

/** The minimum needed to render a customer's name anywhere. */
export type NameableClient = Pick<Client, 'type' | 'firstName' | 'lastName' | 'companyName'>

/**
 * The one place a customer's name is decided.
 *
 * Every list, search result, jobcard header and document goes through this, so
 * a company can never appear as " " because someone read firstName directly.
 */
export function clientDisplayName(client: NameableClient): string {
  if (client.type === 'company') return client.companyName.trim()
  return `${client.firstName} ${client.lastName}`.trim()
}

/** Kept for the individual-only case: the person's name, never a company's. */
export function clientFullName(client: Pick<Client, 'firstName' | 'lastName'>): string {
  return `${client.firstName} ${client.lastName}`.trim()
}

export function isCompany(client: Pick<Client, 'type'>): boolean {
  return client.type === 'company'
}

/** The number to open WhatsApp with: the dedicated one, else the main phone. */
export function whatsappNumberFor(client: Pick<Client, 'phone' | 'whatsappPhone'>): string {
  return client.whatsappPhone ?? client.phone
}

/** Digits only, as the wa.me URL scheme requires. */
/**
 * Put a country code on a number that was typed without one.
 *
 * Staff type what the customer says, and in a workshop that is the local
 * number: "585657171", or "0585657171" off the back of a business card. Both
 * are the same phone, and neither can be dialled from abroad or reach WhatsApp,
 * which needs a full international number. Storing them as typed means the
 * WhatsApp button silently fails for some customers and not others.
 *
 * The rules, in order:
 *
 *   +971 58 565 7171   already international — left exactly as typed, because
 *                      a number that says what it is should not be reformatted
 *   00971585657171     the other way of writing "+" — becomes +971 585657171
 *   971585657171       the code without its plus — becomes +971 585657171
 *   0585657171         national format — the trunk 0 is dropped, since it is
 *                      what the country code replaces
 *   585657171          bare local number — code prepended
 *
 * An empty `defaultCountryCode` turns this off, for a workshop that would
 * rather type numbers itself.
 */
const MIN_SUBSCRIBER_DIGITS = 5

export function normalisePhone(value: string, defaultCountryCode: string): string {
  const trimmed = value.trim()
  if (trimmed === '') return ''

  const code = defaultCountryCode.trim()
  if (code === '') return trimmed
  // A number that already declares its country is not ours to rewrite.
  if (trimmed.startsWith('+')) return trimmed

  const codeDigits = code.replace(/\D/g, '')
  if (codeDigits === '') return trimmed

  // Separators are how humans write numbers; they say nothing about which
  // country it is in, so they are ignored for the decision and dropped from
  // the part that follows the code.
  const digits = trimmed.replace(/\D/g, '')
  if (digits === '') return trimmed

  let national = digits

  /*
   * 00 already means "international", so whatever follows carries its own
   * country code and must not be prefixed again.
   *
   * Only when what follows is long enough to BE a number, though: "000" starts
   * with 00 and is not a country code — reading it as one turned it into "+0".
   */
  if (national.startsWith('00') && national.length - 2 >= MIN_SUBSCRIBER_DIGITS) {
    return `+${national.slice(2)}`
  }

  if (national.startsWith(codeDigits)) {
    const rest = national.slice(codeDigits.length)
    return rest === '' ? trimmed : `+${codeDigits} ${rest}`
  }

  // The trunk prefix is what the country code replaces, so it goes.
  if (national.startsWith('0')) national = national.replace(/^0+/, '')
  if (national === '') return trimmed

  return `+${codeDigits} ${national}`
}

export function toWaMeDigits(phone: string): string {
  return phone.replace(/\D/g, '')
}
